import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg, label, fmtDate, downloadFile } from "../lib/api";
import { useMatch, playersOf } from "../lib/useMatch";
import { MatchHeader } from "../components/MatchHeader";
import { EventTimeline } from "../components/EventTimeline";
import { Pitch, layoutPositions } from "../components/Pitch";
import { Panel, Spinner, Empty, StatusBadge } from "../components/kit";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const TIMELINE = ["scheduled", "awaiting_team_sheets", "teams_submitted", "teams_approved", "officials_confirmed", "ready_for_kickoff", "first_half", "half_time", "second_half", "full_time", "reports_submitted", "under_review", "validated", "archived"];

function Reports({ m, reload }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = () => api.get(`/matches/${m.id}/reports`).then((r) => setD(r.data)).catch((e) => setD({ error: errMsg(e) }));
  useEffect(() => { load(); }, [m.status]); // eslint-disable-line
  if (!d) return <Spinner />;
  if (d.error) return <Empty text={d.error} />;
  const act = async (action) => { try { await api.post(`/matches/${m.id}/validate`, { action, note }); toast.success(`Result: ${label(action)}`); reload(); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2"><Button variant="outline" data-testid="match-pdf-btn" onClick={() => downloadFile(`/matches/${m.id}/report.pdf`, `TRO_${m.match_number}.pdf`)}>Download PDF report</Button></div>
      {m.can_manage && (
        <Panel title="League validation" testid="validation-panel">
          <Textarea data-testid="validation-note" placeholder="Note / clarification request" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="outline" data-testid="review-btn" onClick={() => act("review")}>Mark under review</Button>
            <Button variant="outline" data-testid="clarify-btn" onClick={() => act("clarify")}>Request clarification</Button>
            <Button data-testid="validate-result-btn" onClick={() => act("validate")}>Validate result & publish</Button>
            <Button variant="outline" onClick={() => act("archive")}>Archive</Button>
          </div>
          <div className="mt-2 space-y-1">{d.validation.map((v, i) => <div key={i} className="text-xs">{fmtDate(v.at)} · {label(v.action)} {v.note && `— ${v.note}`}</div>)}</div>
        </Panel>
      )}
      {!d.reports.length && <Empty text="No reports submitted yet." />}
      {d.reports.map((r) => (
        <Panel key={r.type + r.author_id} title={`${label(r.type)} report · ${r.author_name}`} action={<span className="text-xs">{fmtDate(r.submitted_at)} · {r.signed ? "Signed" : "Unsigned"}</span>} testid={`report-${r.type}`}>
          <dl className="grid gap-2 md:grid-cols-2">{Object.entries(r.content).filter(([k]) => k !== "referee_id").map(([k, v]) => <div key={k}><dt className="tro-label">{label(k)}</dt><dd className="text-sm">{String(v) || "—"}</dd></div>)}</dl>
        </Panel>))}
    </div>
  );
}

export default function MatchView({ tab = "live" }) {
  const { id } = useParams();
  const room = useMatch(id);
  const m = room.match;
  const [side, setSide] = useState("home");
  if (!m) return <Spinner />;
  const t = m[side];
  const { lineup, onField } = playersOf(m, t.team_id);
  const idx = TIMELINE.indexOf(m.status);
  return (
    <div className="space-y-4" data-testid="match-view">
      <MatchHeader match={m} elapsed={room.elapsed} conn={room.conn} />
      <div className="tro-card overflow-x-auto p-3" data-testid="prematch-timeline">
        <div className="flex min-w-max gap-1">{TIMELINE.map((s, i) => <span key={s} className={`rounded px-2 py-1 text-[10px] font-bold uppercase ${i <= idx ? "bg-[#17191C] text-white" : "bg-[#F7F8F9] text-[#6C757D]"}`}>{label(s)}</span>)}</div>
        {!["validated", "archived"].includes(m.status) && idx >= TIMELINE.indexOf("full_time") && <div className="mt-2 text-xs font-semibold text-[#6C757D]">Result not yet validated — not final.</div>}
      </div>
      <Tabs defaultValue={tab}>
        <TabsList><TabsTrigger value="live" data-testid="tab-live">Live</TabsTrigger><TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>{m.can_view_private && <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>}</TabsList>
        <TabsContent value="live">
          <div className="grid gap-4 xl:grid-cols-[1fr_380px]">
            <div className="tro-card p-3">
              <div className="mb-3 flex gap-1.5">{["home", "away"].map((s) => <button key={s} onClick={() => setSide(s)} className={`rounded-lg px-4 py-2 text-sm font-bold ${side === s ? "bg-[#17191C] text-white" : "bg-[#F7F8F9]"}`}>{m[s].name}</button>)}</div>
              {lineup ? <Pitch players={onField} positions={layoutPositions(lineup, room.events, m.tactics?.[t.team_id])} state={m.player_state} testid="public-pitch" /> : <Empty text="Line-up not published." />}
            </div>
            <div className="space-y-4">
              {m.official_names && <Panel title="Match officials">{Object.entries(m.official_names).map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span className="tro-label">{label(k)}</span><b>{v}{m.confirmations?.[k] ? " ✓" : ""}</b></div>)}</Panel>}
              <Panel title="Events"><EventTimeline events={room.events} match={m} /></Panel>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="events"><Panel><EventTimeline events={room.events} match={m} /></Panel></TabsContent>
        {m.can_view_private && <TabsContent value="reports"><Reports m={m} reload={room.load} /></TabsContent>}
      </Tabs>
      <div className="text-xs text-[#6C757D]">Venue {m.venue} · Kickoff {fmtDate(m.kickoff)} · <StatusBadge status={m.status} /></div>
    </div>
  );
}
